import { assert, normalize, objectKeys, type ReadonlyDate, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType, type CybusLog, CybusLogLevel } from '../../../domain'

type LogCollectorArgs = Readonly<{ parsed: ReadonlyRecord<string, unknown>, rawTimestamp: string, rawMessage: string }>

class IgnoreSignal extends ConnectwareError<ConnectwareErrorType.UNEXPECTED> {
    constructor () {
        super(ConnectwareErrorType.UNEXPECTED)
    }
}

export class LogsProcessor {
    private static readonly logLevelMap: ReadonlyRecord<string | number, CybusLogLevel> = {
        fatal: CybusLogLevel.FATAL,
        60: CybusLogLevel.FATAL,

        error: CybusLogLevel.ERROR,
        50: CybusLogLevel.ERROR,

        warn: CybusLogLevel.WARN,
        40: CybusLogLevel.WARN,

        info: CybusLogLevel.INFO,
        30: CybusLogLevel.INFO,

        debug: CybusLogLevel.DEBUG,
        20: CybusLogLevel.DEBUG,

        trace: CybusLogLevel.TRACE,
        10: CybusLogLevel.TRACE,
    }

    private readonly search: string

    private readonly levels: Set<CybusLogLevel> | null

    private readonly collectors: { [P in keyof CybusLog]: (args: LogCollectorArgs) => CybusLog[P] } = {
        timestamp: ({ parsed: { time }, rawTimestamp }) => {
            const timestamp = LogsProcessor.parseTimestamp(rawTimestamp) || LogsProcessor.parseTimestamp(time) || null
            assert(timestamp, new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Could not map log', { rawTimestamp, time }))
            assert(!this.end || this.end >= timestamp, new IgnoreSignal())
            return timestamp
        },
        level: ({ parsed: { level } }) => {
            const mappedLevel = ((typeof level === 'string' || typeof level === 'number') && LogsProcessor.logLevelMap[level]) || CybusLogLevel.INFO
            assert(!this.levels || this.levels.has(mappedLevel), new IgnoreSignal())
            return mappedLevel
        },
        message: ({ parsed: { msg }, rawMessage }) => (typeof msg === 'string' ? msg : rawMessage),
        className: ({ parsed: { className } }) => (typeof className === 'string' ? className : null),
        pid: ({ parsed: { pid } }) => (typeof pid === 'number' ? pid : null),
        hostname: ({ parsed: { hostname } }) => (typeof hostname === 'string' ? hostname : null),
        serviceId: ({ parsed: { id } }) => (typeof id === 'string' ? id : null),
    }

    private readonly collectionsNames: (keyof typeof this.collectors)[]

    private static parseTimestamp (source: unknown): ReadonlyDate | null {
        const date = typeof source === 'string' || typeof source === 'number' ? new Date(source) : null
        return date && !Number.isNaN(date.getTime()) ? date : null
    }

    private static parse (line: string): [rawTimestamp: string, parsed: ReadonlyRecord<string, unknown>] {
        const firstSpaceIndex = line.indexOf(' ')

        /** Position of the timestamp */
        const rawTimestamp = line.substring(0, firstSpaceIndex)

        /** Position of the actual data */
        const rawData = line.substring(firstSpaceIndex + 1)

        let parsed: ReadonlyRecord<string, unknown>
        try {
            parsed = JSON.parse(rawData) as typeof parsed
        } catch (e) {
            parsed = { msg: rawData }
        }

        return [rawTimestamp, parsed]
    }

    constructor (search: string | null, levels: ReadonlyNonEmptyArray<CybusLogLevel> | null, private readonly end: ReadonlyDate | null) {
        this.search = normalize(search?.trim() ?? '')
        this.levels = levels && new Set(levels)
        this.collectionsNames = objectKeys(this.collectors)
    }

    /**
     * @returns if the log is relevant for search
     */
    private isLogRelevant (log: CybusLog): boolean {
        if (!this.search) {
            return true
        }

        return Object.values(log).some(
            (value) =>
                /**
                 * Value is searchable
                 */
                (typeof value === 'string' || typeof value === 'number') &&
                /**
                 * Value matches
                 */
                normalize(String(value)).includes(this.search)
        )
    }

    process (text: string): readonly CybusLog[] {
        return text.split('\n').reduce<readonly CybusLog[]>((logs, rawMessage) => {
            if (!rawMessage) {
                return logs
            }

            const [rawTimestamp, parsed] = LogsProcessor.parse(rawMessage)

            try {
                const data: LogCollectorArgs = { parsed, rawTimestamp, rawMessage }

                const log: CybusLog = this.collectionsNames.reduce((log, field) => ({ ...log, [field]: this.collectors[field](data) }), {} as CybusLog)

                if (this.isLogRelevant(log)) {
                    return [...logs, log]
                }
            } catch (e: unknown) {
                if (e instanceof IgnoreSignal) {
                    return logs
                }
                throw e
            }

            return logs
        }, [])
    }
}
